import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import APSLogo from "../../../components/ui/APS Logo.svg";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../../components/ui/Button";
import { navLinks } from "../constants/navigation";

const Navbar = () => {
  const [open,      setOpen]      = useState(false);
  const [scrolled,  setScrolled]  = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0,   opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-md border-b border-surface-border shadow-sm"
          : "bg-white border-b border-surface-border"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <motion.img
            src={APSLogo}
            alt="APS Logo"
            className="h-12 w-auto object-contain"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400 }}
          />
        </Link>

        {/* Desktop nav — hidden (links moved to CTA row) */}

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="flex items-center gap-6"
          >
            {navLinks.map(({ label, href }, i) => (
              <motion.a
                key={label}
                href={href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (i + 1), duration: 0.4 }}
                className="text-sm text-brand-muted hover:text-primary font-medium transition-colors relative group"
              >
                {label}
                <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-primary rounded-full transition-all duration-300 group-hover:w-full" />
              </motion.a>
            ))}
            <Link to="/auth/login" className="text-sm font-medium text-brand-muted hover:text-primary transition-colors">Login</Link>
            <Link to="/auth/register">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button size="sm">Get Started</Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden text-brand-muted" onClick={() => setOpen(!open)}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={open ? "x" : "menu"}
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0,   opacity: 1 }}
              exit={{    rotate: 90,  opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{    height: 0,    opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="md:hidden overflow-hidden border-t border-surface-border bg-white"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map(({ label, href }) => (
                <a key={label} href={href} onClick={() => setOpen(false)} className="text-sm text-brand-muted font-medium hover:text-primary transition-colors">{label}</a>
              ))}
              <Link to="/auth/login" className="text-sm text-brand-muted font-medium hover:text-primary transition-colors">Login</Link>
              <Link to="/auth/register" onClick={() => setOpen(false)}>
                <Button size="sm" className="w-fit">Get Started</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
